import type { ItemFieldRow } from './item-field-reader';
import { createFormulaReadContext } from './formula-read-context';
import type { FormulaReadDependencies } from './formula-read-context';
import { readCreaturePayloadStatuses, readCreatureStatuses } from './creature-status-reader';
import { layerEntryNeedsResolve, resolveLayerEntry } from './layer-entry-resolver';
import { readAbilityEntryLayerSource } from './inventory-ability-grant-helpers';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { foldCreatureDefenseEntries } from './creature-defense';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { phpJsonInteger } from './php-integer-arithmetic';
import { utf8Bytes } from './php-json-serialize';
import { SHAPESHIFT_REMOVE_LAYERS, SHAPESHIFT_ADD_LAYERS } from './shapeshift-layer-lists';

type Row = Record<string, unknown>;
const arrayLike = (v: unknown): v is ItemFieldRow => v !== null && typeof v === 'object';
const own = (v: unknown, k: string): unknown => arrayLike(v) && Object.prototype.hasOwnProperty.call(v, k) ? (v as Row)[k] : undefined;
const valid = (v: unknown): v is string => typeof v === 'string' && /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(v);
const upper = (v: string): string => v.replace(/[a-z]/g, c => c.toUpperCase());
function clone<T>(v: T): T {
    if (!arrayLike(v)) return v;
    const entries = Object.entries(v);
    return (entries.every(([k], i) => k === String(i)) ? entries.map(([, child]) => clone(child))
        : Object.fromEntries(entries.map(([k, child]) => [k, clone(child)]))) as T;
}
export class ShapeshiftArrayWriteError extends Error {}
export class ShapeshiftIterationError extends Error {}
function writable(v: unknown): Row {
    if (v == null || v === false) return {};
    if (!arrayLike(v)) {
        if (typeof v === 'string') throw new TypeError('Cannot access offset of type string on string');
        throw new ShapeshiftArrayWriteError('Cannot use scalar as array');
    }
    return Object.fromEntries(Object.entries(v));
}
function write(row: Row, path: string[], value: unknown): void {
    const [key, ...rest] = path;
    if (key === undefined) throw new Error('Missing write path');
    if (!rest.length) { Object.defineProperty(row, key, { value, writable: true, enumerable: true, configurable: true }); return; }
    const child = writable(row[key]); write(child, rest, value); row[key] = child;
}
function drop(row: Row, path: string[]): void {
    const [key, ...rest] = path; if (key === undefined) return;
    if (!rest.length) { delete row[key]; return; }
    const child = row[key];
    if (typeof child === 'string') throw new ShapeshiftArrayWriteError('Cannot unset string offsets');
    if (!arrayLike(child)) {
        if (child != null && child !== false) throw new ShapeshiftArrayWriteError('Cannot unset scalar offsets');
        return;
    }
    const copy = writable(child); drop(copy, rest); row[key] = copy;
}
function append(row: Row, key: string, value: unknown): void {
    const list = writable(row[key]);
    let next = 0;
    for (const k of Object.keys(list)) if (/^(?:0|-?[1-9]\d*)$/.test(k) && Number.isSafeInteger(Number(k))) next = Math.max(next, Number(k) + 1);
    list[String(next)] = value; row[key] = list;
}
export interface ShapeshiftMetadata {
    classMechanics: readonly { item_id: string }[];
    skillTiers: readonly { variable_name: string; skill_tier_level: number }[];
    innateSkills: readonly { item_id: string }[];
    naturalSkills: readonly { item_id: string }[];
    skillRegistry: Readonly<Record<string, { item_id: string }>>;
    defenseSlots: Readonly<Record<string, string>>;
}
export interface CreatureShapeshiftDependencies extends FormulaReadDependencies {
    metadata: ShapeshiftMetadata;
    queryFullItem: (id: string) => ItemFieldRow | null;
    /** These source-producing stages remain explicit; the shapeshift ordering,
     * transfer, picture choice and final row writes are owned here. */
    applyMonsterTemplate: (form: ItemFieldRow, id: string) => ItemFieldRow;
    applyCreatureModelMedia: (form: ItemFieldRow) => ItemFieldRow;
    picturesOf: (form: ItemFieldRow) => ItemFieldRow;
}
/** Deterministic native unsigned crc32 seed (UTF-8 bytes, including empty input). */
function crc32(value: string): number {
    let crc = 0xffffffff;
    for (const byte of utf8Bytes(value)) {
        crc ^= byte;
        for (let bit = 0; bit < 8; bit++) crc = (crc >>> 1) ^ ((crc & 1) ? 0xedb88320 : 0);
    }
    return (crc ^ 0xffffffff) >>> 0;
}
/** Full check_shapeshifts_for_item_arr, including replace_skills_by_tier.
 * Compiles a copy. Actual definition/overlay/media replies are explicit inputs;
 * missing replies must throw, never silently certify an unexercised branch. */
export function createCreatureShapeshiftCompiler(deps: CreatureShapeshiftDependencies): (input: ItemFieldRow) => ItemFieldRow {
    const readers = createFormulaReadContext(deps), meta = deps.metadata;
    const reference = (id: string): ItemFieldRow | null => { deps.trace?.(['reference', id]); return deps.resolveCarrierRef(id); };
    const query = (id: string): ItemFieldRow | null => { deps.trace?.(['full-item', id]); return deps.queryFullItem(id); };
    const has = (row: ItemFieldRow, key: string): boolean => readers.layerValue(row, key) !== null || phpJsonTruthy(readers.layerEntries(row, key));
    const entryReaders = { itemField: readers.itemField, resolveCarrierRef: reference };
    const mechanicIds = new Set(meta.classMechanics.map(s => upper(phpJsonString(s.item_id ?? ''))).filter(Boolean));
    function replaceSkills(row: Row, form: ItemFieldRow, skills: readonly { item_id: string }[]): Row {
        const hostMap = new Map<string, { level: bigint; key: string }>(), formMap = new Map<string, { level: bigint; key: string }>();
        const compiled = own(own(own(row, 'computed'), 'creature'), 'skills') ?? row.skills ?? [];
        if (!arrayLike(compiled)) throw new TypeError('caster_skills must return array');
        for (const [variable, entry] of Object.entries(compiled)) {
            if (variable === '_meta' || !arrayLike(entry)) continue;
            const id = phpJsonString(own(entry, 'item_id') ?? meta.skillRegistry[variable]?.item_id ?? ''), level = phpJsonInteger(own(entry, 'tier_level') ?? 0);
            if (id !== '' && level > 0n) hostMap.set(id, { level, key: 'skill_tier_' + String(level) });
        }
        for (const tier of meta.skillTiers) {
            const key = tier.variable_name, level = BigInt(tier.skill_tier_level);
            for (const [target, map] of [[row, hostMap], [form, formMap]] as const) for (const entry of Object.values(readers.layerEntries(target, key))) {
                const id = readLayerListEntryUuid(entry);
                if (id !== '' && level > (map.get(id)?.level ?? 0n)) map.set(id, { level, key });
            }
        }
        for (const skill of skills) {
            const shift = formMap.get(skill.item_id), host = hostMap.get(skill.item_id);
            if (shift && (!host || shift.level > host.level)) append(row, shift.key, skill.item_id);
        }
        return row;
    }
    return input => {
        if (!arrayLike(input)) throw new TypeError('Shapeshift requires array');
        let row = writable(clone(input));
        const statuses = (): ItemFieldRow => readCreatureStatuses(row, 'target', reference);
        const payloadStatuses = (): ItemFieldRow => readCreaturePayloadStatuses(row, readers.layerValue, reference);
        if (!phpJsonTruthy(statuses()) || !Object.values(statuses()).map(e => readers.itemField(e as ItemFieldRow, 'item_type') ?? '').includes('creature')) {
            drop(row, ['computed', 'creature', 'shapeshift_charge_mods']); return clone(row);
        }
        write(row, ['shapeshifted', '0'], 1); drop(row, ['computed', 'creature', 'shapeshift_pic']);
        const carried: Record<string, unknown[]> = {}, carriedKeys: Record<string, Set<string>> = {};
        for (const kind of deps.abilityTypes) {
            if (row[kind] == null) continue;
            if (!arrayLike(row[kind])) throw new ShapeshiftIterationError('Ability bucket must be iterable');
            for (const [key, raw] of Object.entries(row[kind] as ItemFieldRow)) {
                // String entries fail on a named-offset write only if resolution fires.
                let entry: unknown = clone(raw);
                const nested = own(entry, 'item_arr') ?? own(entry, 'item_id') ?? null;
                if (layerEntryNeedsResolve(nested)) {
                    const copy = writable(entry); copy.item_arr = resolveLayerEntry(nested, row, query); entry = copy;
                }
                const def = own(entry, 'item_arr');
                let transfer = arrayLike(def) && (readers.itemField(def, 'item_type') ?? '') === 'class_mechanic';
                if (!transfer) for (const id of [own(entry, 'ability_id') ?? '', arrayLike(def) ? readLayerListEntryUuid(def) : '', readLayerListEntryUuid(entry)]) {
                    const value = upper(phpJsonString(id)); if (value !== '' && mechanicIds.has(value)) { transfer = true; break; }
                }
                if (transfer || (arrayLike(def) && has(def, 'ability_transferred_while_shapeshifted'))) {
                    (carried[kind] ??= []).push(entry); (carriedKeys[kind] ??= new Set()).add(key);
                }
            }
        }
        const mods: unknown[] = []; let scanned = false;
        for (const kind of ['passive_ability', 'feat']) {
            if (!phpJsonTruthy(row[kind]) || !arrayLike(row[kind])) continue; scanned = true;
            for (const [key, entry] of Object.entries(row[kind] as ItemFieldRow)) {
                if (!arrayLike(entry) || carriedKeys[kind]?.has(key)) continue;
                const source = readAbilityEntryLayerSource(entry, entryReaders);
                for (const [selector, quantity] of [['increase_max_charges_of_ability', 'increase_max_charges_by_qty'], ['decrease_max_charges_of_ability', 'decrease_max_charges_by_qty']] as const) {
                    const ids = [...new Set(Object.values(readers.layerEntries(source, selector)).map(readLayerListEntryUuid).filter(valid))].filter(id => mechanicIds.has(upper(id)));
                    if (!ids.length) continue;
                    const layers: Row = { [selector]: ids }, qty = readers.layerValue(source, quantity);
                    if (qty !== null) layers[quantity] = qty;
                    mods.push({ item_arr: { item_layer_types: layers } });
                }
            }
        }
        if (scanned) { if (mods.length) write(row, ['computed', 'creature', 'shapeshift_charge_mods'], mods); else drop(row, ['computed', 'creature', 'shapeshift_charge_mods']); }
        for (const key of SHAPESHIFT_REMOVE_LAYERS) delete row[key];
        drop(row, ['computed', 'creature', 'defenses']);
        for (const key of ['background_body', 'background_mind', 'background_soul', 'background_fortune']) write(row, ['item_layer_types', key], 0);
        for (const effect of Object.values(payloadStatuses())) {
            if ((readers.itemField(effect as ItemFieldRow, 'item_type') ?? '') !== 'creature') continue;
            let form = own(effect, 'status_arr');
            if (!arrayLike(form)) throw new TypeError('Shapeshift form must be array');
            form = clone(form);
            const template = own(effect, 'status_shapeshift_monster_template_id') ?? '';
            if (valid(template)) {
                form = deps.applyMonsterTemplate(form as ItemFieldRow, template);
                if (!arrayLike(form)) throw new TypeError('Template overlay must return array');
                const surfaced = writable(form);
                for (const key of SHAPESHIFT_ADD_LAYERS) { const entries = readers.layerEntries(surfaced, key); if (phpJsonTruthy(entries)) surfaced[key] = entries; }
                form = surfaced;
                const media = deps.applyCreatureModelMedia(clone(form as ItemFieldRow));
                const pictures = Object.values(deps.picturesOf(media));
                pictures.sort((a, b) => {
                    if ((a !== null && !arrayLike(a)) || (b !== null && !arrayLike(b))) throw new TypeError('pic_field requires array');
                    const left = utf8Bytes(phpJsonString(own(a, 'picture_id') ?? '')), right = utf8Bytes(phpJsonString(own(b, 'picture_id') ?? ''));
                    for (let i = 0; i < Math.min(left.length, right.length); i++) if (left[i] !== right[i]) return left[i]! - right[i]!;
                    return left.length - right.length;
                });
                if (pictures.length) { const seed = phpJsonString(own(effect, 'status_id') ?? ''); write(row, ['computed', 'creature', 'shapeshift_pic'], pictures[seed !== '' ? crc32(seed) % pictures.length : 0]); }
            }
            for (const key of SHAPESHIFT_ADD_LAYERS) for (const value of Object.values(readers.layerEntries(form as ItemFieldRow, key))) append(row, key, value);
            for (const [layer, slot] of Object.entries(meta.defenseSlots)) {
                const rows = own(own(own(own(form, 'computed'), 'creature'), 'defenses'), slot) ?? [];
                if (!arrayLike(rows)) continue;
                const damage = ['damage_type_resistance', 'damage_type_immunity', 'damage_type_vulnerability'].includes(layer);
                const add = (id: string, source: Row): void => {
                    if (id === '') return;
                    const existing = own(own(own(row.computed, 'creature'), 'defenses'), slot) ?? [];
                    const folded = foldCreatureDefenseEntries({ family: damage ? 'damage' : 'status', initialEntries: Object.values(existing as ItemFieldRow), events: [{ uuid: id, source }] });
                    write(row, ['computed', 'creature', 'defenses', slot], folded);
                };
                for (const entry of Object.values(rows)) {
                    if (arrayLike(entry)) {
                        const id = phpJsonString(damage ? own(entry, 'damage_type') ?? '' : own(entry, 'item_layer_effect') ?? readLayerListEntryUuid(entry));
                        if (id === '') continue;
                        const source = Object.fromEntries(['source_item_id', 'source_name', 'source_kind', 'source_origin_item_id', 'source_origin_name'].map(k => [k, own(entry, k) ?? null]));
                        add(id, source);
                        const all = own(entry, 'all_sources') ?? [];
                        if (!arrayLike(all)) throw new ShapeshiftIterationError('Defense contributors must be iterable');
                        for (const contribution of Object.values(all)) if (arrayLike(contribution)) add(id, contribution as Row);
                    } else if (typeof entry === 'string' && entry !== '') add(entry, {});
                }
            }
            row = replaceSkills(row, form as ItemFieldRow, meta.innateSkills);
            row = replaceSkills(row, form as ItemFieldRow, meta.naturalSkills);
            break;
        }
        for (const kind of deps.abilityTypes) for (const entry of carried[kind] ?? []) append(row, kind, entry);
        return clone(row);
    };
}
